<template>
  <div class="bg-white border border-black/6 rounded-[20px] p-6 shadow-[0_2px_12px_rgba(0,0,0,0.04)]">
    <div class="flex items-center gap-2 mb-5">
      <span class="text-[11px] font-bold uppercase tracking-[0.06em] text-muted">Activity</span>
      <span v-if="activities.length" class="bg-[#f0eef5] text-primary text-[11px] font-semibold py-0.5 px-2 rounded-full">{{ activities.length }}</span>
    </div>

    <div v-if="isLoading" class="flex flex-col gap-3 py-2">
      <div v-for="i in 3" :key="i" class="flex gap-3 py-3" :class="{ 'border-t border-[#f5f3f7]': i > 1 }">
        <skeleton width="30px" height="30px" rounded="50%" class-name="shrink-0"></skeleton>
        <div class="flex-1">
          <div class="flex items-center gap-2 mb-1.5">
            <skeleton width="60px" height="13px" rounded="4px"></skeleton>
            <skeleton width="120px" height="13px" rounded="4px"></skeleton>
          </div>
          <skeleton width="50px" height="11px" rounded="4px"></skeleton>
        </div>
      </div>
    </div>

    <div v-else-if="activities.length === 0" class="flex flex-col items-center py-6 text-muted">
      <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><polyline points="12 6 12 12 16 14"></polyline></svg>
      <p class="text-[13px] text-placeholder mt-2.5 mb-0">No activity yet</p>
    </div>

    <div v-else class="flex flex-col">
      <div
        v-for="activity in visibleActivities"
        :key="activity.id"
        class="group flex gap-3 py-3 border-t border-[#f5f3f7] first:border-t-0">

        <img v-if="getActivityPhoto(activity)" :src="getActivityPhoto(activity)" class="shrink-0 w-[30px] h-[30px] rounded-full object-cover mt-0.5">
        <div v-else class="shrink-0 w-[30px] h-[30px] rounded-full bg-[#f0eef5] text-primary flex items-center justify-center font-bold text-xs mt-0.5">{{ activity.user ? activity.user.charAt(0) : '?' }}</div>

        <div class="flex-1 min-w-0">
          <div class="text-sm text-ink leading-snug">
            <strong v-if="activity.user" class="font-semibold mr-1">{{ activity.user }}</strong>
            <span>{{ activity.message }}</span>
          </div>
          <p v-if="activity.details" class="text-[13px] text-[#645B66] mt-1.5 mb-0 leading-relaxed bg-canvas py-2.5 px-3.5 rounded-lg whitespace-pre-wrap break-words" v-html="linkify(activity.details)"></p>
          <div v-if="activity.changes && activity.changes.length" class="mt-1.5 bg-canvas rounded-lg py-2 px-3.5">
            <div v-for="(change, ci) in activity.changes" :key="ci" class="flex items-baseline gap-2 text-[12px] leading-relaxed flex-wrap" :class="{ 'mt-0.5': ci > 0 }">
              <span class="text-placeholder font-medium shrink-0">{{ change.field }}</span>
              <span class="text-placeholder line-through break-all">{{ change.from || '–' }}</span>
              <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#b5b0b8" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="shrink-0"><line x1="5" y1="12" x2="19" y2="12"></line><polyline points="12 5 19 12 12 19"></polyline></svg>
              <span class="text-ink font-semibold break-all">{{ change.to || '–' }}</span>
            </div>
          </div>
          <span class="text-xs text-placeholder mt-1 inline-block">{{ formatTimeAgo(activity.createdAt) }}</span>
        </div>

        <button
          v-if="user && user.isAdmin"
          class="shrink-0 bg-transparent border-0 text-muted cursor-pointer p-1 rounded-md opacity-0 transition-[opacity,color] duration-150 self-start mt-0.5 group-hover:opacity-100 hover:!text-[#e53935]"
          :class="{ '!opacity-100 !text-[#e53935] text-xs font-semibold': confirmDeleteId === activity.id }"
          @click.stop="deleteActivity(activity)">
          <span v-if="confirmDeleteId === activity.id">Delete?</span>
          <svg v-else xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path></svg>
        </button>
      </div>
      <button
        v-if="activities.length > showCount"
        type="button"
        class="w-full text-center text-[13px] font-medium text-primary bg-transparent border-0 cursor-pointer py-2.5 mt-1 rounded-lg transition-colors duration-150 hover:bg-canvas"
        @click="showCount += 15">
        Show {{ Math.min(activities.length - showCount, 15) }} more
      </button>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import { PHOTOS } from '@/services/photos'
import Skeleton from '@/components/UIComponents/Skeleton'
dayjs.extend(relativeTime)

export default {
  name: 'activity-timeline',
  components: { Skeleton },
  props: {
    applicantId: {
      type: String,
      required: true
    }
  },
  data () {
    return {
      isLoading: false,
      unsubscribe: null,
      confirmDeleteId: null,
      showCount: 15
    }
  },
  computed: {
    ...mapState({
      user: state => state.auth.user,
      activities: state => state.activities.activities
    }),
    visibleActivities () {
      return this.activities.slice(0, this.showCount)
    },
    userInitial () {
      const name = this.user && (this.user.firstName || this.user.username)
      return name ? name.charAt(0) : '?'
    }
  },
  methods: {
    getActivityPhoto (activity) {
      if (!activity.user) return null
      return PHOTOS[activity.user.toLowerCase()] || null
    },
    formatTimeAgo (date) {
      if (!date) return ''
      return dayjs(date).fromNow()
    },
    linkify (text) {
      if (!text) return ''
      const escaped = text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      return escaped.replace(/(https?:\/\/[^\s<]+)/g, '<a href="$1" target="_blank" class="text-primary underline hover:opacity-80">$1</a>')
    },
    deleteActivity (activity) {
      if (this.confirmDeleteId === activity.id) {
        this.confirmDeleteId = null
        this.$store.dispatch('DELETE_ACTIVITY', {
          applicantId: this.applicantId,
          activityId: activity.id
        })
      } else {
        this.confirmDeleteId = activity.id
      }
    }
  },
  created () {
    if (this.applicantId) {
      this.isLoading = true
      this.unsubscribe = this.$store.dispatch('GET_ACTIVITIES', {
        applicantId: this.applicantId
      }).then((unsub) => {
        this.isLoading = false
        return unsub
      }).catch(() => {
        this.isLoading = false
      })
    }
    this._resetConfirm = () => { this.confirmDeleteId = null }
    document.addEventListener('click', this._resetConfirm)
  },
  beforeDestroy () {
    this.$store.commit('CLEAR_ACTIVITIES')
    if (this.unsubscribe && typeof this.unsubscribe === 'function') {
      this.unsubscribe()
    }
    document.removeEventListener('click', this._resetConfirm)
  }
}
</script>
